import React from 'react';
import { clientWorkspaceHref } from '../../../utils/clientWorkspace';
import { clientAge } from '../../../services/clientSessionStats';
import { 
  Alert, Stack, FormControl, Select, MenuItem, TextField, Button, Typography,
  TableContainer, Paper, Table, TableHead, TableRow, TableCell, TableBody, Chip, Tooltip 
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Link } from 'react-router-dom';
import SearchBar from '../../../components/Search.js';
import {
  RESTRICTED_ACCESS_LABEL,
  RESTRICTED_ACCESS_TOOLTIP,
} from '../../../utils/scribeConnection.js';

// Data is hidden either way, but the reason matters: a therapist paying for an
// access code must not read "not connected" and conclude the link never
// happened. `awaitingConsent` = paid, client hasn't switched on sharing.
const RestrictedCell = ({ mv, iconOnly = false }) => {
  const paid = !!mv?.awaitingConsent;
  const tooltip = paid
    ? RESTRICTED_ACCESS_TOOLTIP
    : 'No MindVault connection with this client. A booking or an access code, plus the client\'s consent, is required.';
  const label = paid ? RESTRICTED_ACCESS_LABEL : 'Not connected';
  const colors = paid
    ? { backgroundColor: '#E6F7F6', color: '#0F766E', border: '1px solid #99E3DF' }
    : { backgroundColor: '#F3F4F6', color: '#6B7280', border: '1px solid #D1D5DB' };

  return (
    <Tooltip title={tooltip} arrow>
      {iconOnly ? (
        <LockOutlinedIcon sx={{ fontSize: 16, color: paid ? '#0F766E' : '#9CA3AF' }} />
      ) : (
        <Chip
          icon={<LockOutlinedIcon />}
          label={label}
          size="small"
          sx={{ ...colors, fontWeight: 600 }}
        />
      )}
    </Tooltip>
  );
};

const MentalViewMindVault = ({
  timeRange, setTimeRange, customFrom, setCustomFrom, customTo, setCustomTo,
  currentOrg, userDetails, setAlert, setNewPatient, clearFields, teamMembersData, setCaseOwner,
  searchText, handleSearchChange,
  filteredItems, page, rowsPerPage, setPage, handlePageClick,
  corporateMap, getLastConsultationComplaint,
  therapyStats = {}, therapyLoading = false, therapyError = false, onRetryTherapy,
  selectedCaseOwnerFilter, setSelectedCaseOwnerFilter, currentUserRole,
  isVisualTour = false,
}) => {
  const isAdmin = currentUserRole === 'Admin';

  const handleCaseOwnerChange = (e) => {
    const value = e.target.value;
    if (!isAdmin && value === 'ALL') {
      setAlert('Only team admins can view all client records.', 'warning');
      return;
    }
    if (!isAdmin && value !== userDetails?.id) {
      setAlert('Only team admins can view client records of other team members.', 'warning');
      return;
    }
    setSelectedCaseOwnerFilter(value);
  };
  return (
    <>
      {/* Corporate Filters */}
      <Stack direction="row" spacing={2} sx={{ mb: 2, alignItems: 'center' }}>
        <FormControl size="small">
          <Select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            sx={{ borderRadius: 2, minWidth: 160 }}
          >
            <MenuItem value="week">This Week</MenuItem>
            <MenuItem value="month">This Month</MenuItem>
            <MenuItem value="quarter">This Quarter</MenuItem>
            <MenuItem value="custom">Custom</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small">
          <Select
            value={selectedCaseOwnerFilter}
            onChange={handleCaseOwnerChange}
            sx={{ borderRadius: 2, minWidth: 200 }}
            displayEmpty
          >
            <MenuItem
              value="ALL"
              sx={!isAdmin ? { color: 'text.disabled', opacity: 0.5 } : {}}
            >
              All Members
            </MenuItem>
            {(teamMembersData || []).map((member) => {
              const restricted = !isAdmin && member.id !== userDetails?.id;
              return (
                <MenuItem
                  key={member.id}
                  value={member.id}
                  sx={restricted ? { color: 'text.disabled', opacity: 0.5 } : {}}
                >
                  {member.name}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        {timeRange === 'custom' && (
          <Stack direction="row" spacing={1}>
            <TextField
              size="small"
              type="date"
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
            />
            <TextField
              size="small"
              type="date"
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
            />
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setCustomFrom('');
                setCustomTo('');
              }}
            >
              Reset
            </Button>
          </Stack>
        )}
      </Stack>

      <div style={{ padding: '16px', display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
        <Button
          disabled={isVisualTour}
          onClick={() => {
            if(!currentOrg) {
              setAlert('Please select an organization or go to settings and create an organization first.', 'error');
              return;
            }
            if (!userDetails?.name || !userDetails.name.trim()) {
              setAlert('Please set your username in Profile settings before creating a client.', 'error');
              return;
            }
            setNewPatient(true);
            clearFields();
            const currentTeamMember = teamMembersData.find(member => member.id === userDetails.id);
            if (currentTeamMember) {
              setCaseOwner([currentTeamMember]);
            } else {
              setCaseOwner([]);
            }
          }}
          variant="outlined"
          startIcon={<AddIcon sx={{color: '#fff'}}/>}
          sx={{marginRight: '20px', borderRadius: 30, backgroundColor: '#29C0BB', border: 0}}
        >
          <Typography variant="button" sx={{color: '#FFFFFF'}}>New Client</Typography>
        </Button>
        <SearchBar bgcolor='#E4E7EB' placeholder="Search for a client" width="30vw" value={searchText} onChange={handleSearchChange}/>
      </div>

      {therapyError && <Alert severity="warning" sx={{ mb: 2 }} action={
        <Button color="inherit" size="small" onClick={onRetryTherapy}>Retry</Button>
      }>Completed session information could not be loaded. Please retry.</Alert>}
      <TableContainer data-tour={isVisualTour ? 'demo-patient-row' : undefined} component={Paper} sx={{ borderRadius: '16px', width: '100%' }}>
        <Table sx={{ minWidth: 1180 }}>
          <TableHead>
            <TableRow>
              <TableCell align="left"><Typography>Name</Typography></TableCell>
              <TableCell align="left"><Typography>Age</Typography></TableCell>
              <TableCell align="left"><Typography>Gender</Typography></TableCell>
              <TableCell align="left"><Typography>MindVault Client</Typography></TableCell>
              <TableCell align="left"><Tooltip title="Most recent completed appointment across all dates. Completed follows Calendar: marked completed, or ended and not cancelled."><Typography>Last Session Date</Typography></Tooltip></TableCell>
              <TableCell align="left"><Typography>Chat Risk Flags</Typography></TableCell>
              <TableCell align="left"><Typography>Presenting Concern</Typography></TableCell>
              <TableCell align="left"><Tooltip title="Completed appointments in the selected date range."><Typography>Sessions Completed</Typography></Tooltip></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredItems
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((patient, index) => {
                const mv = corporateMap[patient.id];
                const sessions = therapyStats[patient.id];
                const sessionPlaceholder = therapyLoading ? 'Loading…' : '—';
                const gender = typeof patient.gender === 'string' && !patient.gender.startsWith('\\x') ? patient.gender.trim() : '';
                const hasMindVault = typeof mv?.linked === 'boolean' ? mv.linked : null;
                const concern = typeof patient.presenting_concerns === 'string'
                  && !patient.presenting_concerns.startsWith('\\x')
                  ? patient.presenting_concerns.replace(/\s+/g, ' ').trim() : '';

                return (
                  <TableRow
                    key={index}
                    component={Link}
                    to={isVisualTour || !currentOrg ? '/consultationSummary' : clientWorkspaceHref(patient.id, currentOrg.id)}
                    state={{
                      params: {
                        patient: patient,
                        latestComplaint: getLastConsultationComplaint(patient.id),
                        teamMembersData: teamMembersData,
                      },
                    }}
                    sx={{
                      cursor: 'pointer',
                      textDecoration: 'none',
                      color: 'inherit',
                      '&:hover': { backgroundColor: '#f5f5f5' },
                    }}
                    onClick={isVisualTour ? (event) => event.preventDefault() : undefined}
                  >
                    <TableCell align="left">
                      {`${patient.lastName?.toUpperCase() ?? ''} ${patient.firstName ?? ''}`.trim()}
                      {isVisualTour && mv?.linked && !mv?.accessRestricted && <Chip label="Connected · sharing enabled" size="small" sx={{ display: 'flex', width: 'fit-content', mt: 1, bgcolor: '#E6FFFB', color: '#0F766E' }} />}
                    </TableCell>

                    <TableCell align="left">{clientAge(patient)}</TableCell>
                    <TableCell align="left">{gender || '—'}</TableCell>
                    <TableCell align="left">{hasMindVault === null ? '—' : <Chip size="small" variant="outlined"
                      label={hasMindVault ? 'Yes' : 'No'} color={hasMindVault ? 'success' : 'default'} />}</TableCell>
                    <TableCell align="left">{sessions ? sessions.lastSessionDate
                      ? new Date(sessions.lastSessionDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                      : '—' : sessionPlaceholder}</TableCell>

                    <TableCell align="left">
                      {mv?.linked && !mv?.accessRestricted ? (
                        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ rowGap: 0.5 }}>
                          {mv.chatRiskSignals?.HIGH?.length > 0 && (
                            <Tooltip title={`${mv.chatRiskSignals.HIGH.join(', ')}`} arrow>
                              <Chip
                                icon={<ChatBubbleOutlineIcon />}
                                label={`${mv.chatRiskSignals.HIGH.join(', ')}`}
                                size="small"
                                sx={{
                                  backgroundColor: '#FEE2E2',
                                  color: '#B91C1C',
                                  fontWeight: 600,
                                  maxWidth: 260,
                                  '& .MuiChip-label': {
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  },
                                }}
                              />
                            </Tooltip>
                          )}
                          {mv.chatRiskSignals?.MEDIUM?.length > 0 && (
                            <Tooltip title={`${mv.chatRiskSignals.MEDIUM.join(', ')}`} arrow>
                              <Chip
                                icon={<ChatBubbleOutlineIcon />}
                                label={`${mv.chatRiskSignals.MEDIUM.join(', ')}`}
                                size="small"
                                sx={{
                                  backgroundColor: '#FEF3C7',
                                  color: '#92400E',
                                  fontWeight: 600,
                                  maxWidth: 260,
                                  '& .MuiChip-label': {
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  },
                                }}
                              />
                            </Tooltip>
                          )}
                          {(!mv.chatRiskSignals?.HIGH?.length && !mv.chatRiskSignals?.MEDIUM?.length) && (
                            <Typography variant="caption" color="text.secondary">No chat flags</Typography>
                          )}
                        </Stack>
                      ) : mv?.accessRestricted ? (
                        <RestrictedCell mv={mv} />
                      ) : (
                        <Typography variant="caption" color="text.secondary">—</Typography>
                      )}
                    </TableCell>

                    <TableCell align="left" sx={{ maxWidth: 240 }}>
                      {concern ? <Tooltip title={concern} arrow>
                        <Typography noWrap tabIndex={0}>{concern}</Typography>
                      </Tooltip> : '—'}
                    </TableCell>
                    <TableCell align="left">{sessions ? sessions.completedCount : sessionPlaceholder}</TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
        <div style={{ 
          padding: '16px', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center' 
        }}>
          <Typography>
            Showing data {page * rowsPerPage + 1} to {Math.min((page + 1) * rowsPerPage, filteredItems.length)} of {filteredItems.length} entries
          </Typography>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              sx={{
                minWidth: '40px',
                height: '40px',
                backgroundColor: '#f0f0f0',
                '&:hover': { backgroundColor: '#e0e0e0' }
              }}
            >
              ←
            </Button>
            {[...Array(Math.ceil(filteredItems.length / rowsPerPage))].map((_, index) => {
              const pageNum = index + 1;
              const totalPages = Math.ceil(filteredItems.length / rowsPerPage);
              const showPage = pageNum <= 3 || 
                            pageNum > totalPages - 3 || 
                            Math.abs(pageNum - (page + 1)) <= 1;

              if (!showPage && pageNum === 4) {
                return <Typography key="ellipsis1">...</Typography>;
              }
              if (!showPage && pageNum === totalPages - 3) {
                return <Typography key="ellipsis2">...</Typography>;
              }
              if (!showPage) return null;

              return (
                <Button
                  key={pageNum}
                  onClick={() => handlePageClick(pageNum)}
                  sx={{
                    minWidth: '40px',
                    height: '40px',
                    backgroundColor: page === index ? '#29C0BB' : '#f0f0f0',
                    color: page === index ? 'white' : 'inherit',
                    '&:hover': { backgroundColor: page === index ? '#29C0BB' : '#e0e0e0' }
                  }}
                >
                  {pageNum}
                </Button>
              )
            })}
            <Button
              onClick={() => setPage(Math.min(Math.ceil(filteredItems.length / rowsPerPage) - 1, page + 1))}
              disabled={page >= Math.ceil(filteredItems.length / rowsPerPage) - 1}
              sx={{
                minWidth: '40px',
                height: '40px',
                backgroundColor: '#f0f0f0',
                '&:hover': { backgroundColor: '#e0e0e0' }
              }}
            >
              →
            </Button>
          </div>
        </div>
      </TableContainer>
    </>
  );
};

export default MentalViewMindVault;
